<template>
  <transition name="order-detail-dialog-fade">
    <div v-if="visible" class="order-detail-dialog" @click.self="handleClose">
      <div class="panel">
        <button type="button" class="close" aria-label="关闭订单详情弹窗" @click="handleClose">
          <img :src="assets.detailCloseIcon" alt="" class="close-icon" />
        </button>

        <div class="base">
          <div class="base-item">
            <p class="base-label">订单编号</p>
            <p class="base-value">
              {{ displayOrder.orderNo || displayOrder.id }}
            </p>
          </div>
          <div class="base-item">
            <p class="base-label">案由名称</p>
            <p class="base-value">
              {{ displayOrder.caseReason }}
            </p>
          </div>
          <div class="base-item">
            <p class="base-label">所属省或直辖市名称</p>
            <p class="base-value">{{ displayOrder.location }}</p>
          </div>
          <div class="base-item">
            <p class="base-label">购买判例时间区间</p>
            <p class="base-value">{{ displayOrder.dateRange }}</p>
          </div>
        </div>

        <p class="section-title">
          案由列表（{{ displayOrder.detailCases.length }}条）
        </p>

        <div class="cases">
          <div v-for="item in displayCases" :key="item.rank" class="case" :class="{ 'is-expanded': item.expanded }">
            <template v-if="item.expanded">
              <button type="button" class="case-trigger case-head" @click="toggleCase(item.rank)">
                <div class="badge">{{ item.rank }}</div>
                <img :src="assets.detailActiveLine" alt="" class="case-line case-line-active" />
                <img :src="assets.detailExpandIcon" alt="" class="case-arrow is-expanded" />
              </button>

              <div class="case-divider">
                <img :src="assets.detailDivider" alt="" />
              </div>

              <div class="case-grid">
                <p class="label">案由</p>
                <p class="value">{{ item.cause }}</p>
                <p class="label">案件类型</p>
                <p class="value">{{ item.caseType }}</p>

                <p class="label">审理法院</p>
                <p class="value">{{ item.court }}</p>
                <p class="label">案号</p>
                <p class="value">{{ item.caseNo }}</p>

                <p class="label">裁判日期</p>
                <p class="value">{{ item.judgeDate }}</p>
                <p class="label">审理程序</p>
                <p class="value">{{ item.procedure }}</p>

                <p class="label">当事人</p>
                <p class="value value-wide">
                  {{ item.parties }}
                </p>
                <p class="label">标的额</p>
                <p class="value">{{ item.amount }}</p>

                <p class="label">裁判要旨</p>
                <p class="value value-full">
                  {{ item.summary }}
                </p>

                <p class="label">法律依据</p>
                <p class="value value-full">
                  {{ item.basis }}
                </p>
              </div>
            </template>

            <template v-else>
              <button type="button" class="case-trigger case-collapsed" @click="toggleCase(item.rank)">
                <div class="badge">{{ item.rank }}</div>
                <img :src="item.lineType === 'long'
                  ? assets.detailCollapsedLong
                  : assets.detailCollapsedShort
                  " alt="" class="case-line" />
                <img :src="assets.detailExpandIcon" alt="" class="case-arrow" />
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
function createEmptyOrder() {
  return {
    id: "",
    orderId: "",
    orderNo: "",
    caseReason: "",
    location: "",
    dateRange: "",
    detailCases: [],
  };
}

function formatDateRange(item) {
  const startDate =
    item.startDate || item.start_date || item.judgmentStartDate || "";
  const endDate = item.endDate || item.end_date || item.judgmentEndDate || "";

  if (startDate || endDate) {
    return [startDate, endDate].filter(Boolean).join("至");
  }

  return item.dateRange || item.date_range || "--";
}

function formatAmount(item) {
  const rawAmount =
    item.totalPrice ||
    item.total_price ||
    item.pay_amount ||
    item.amount ||
    item.price ||
    "";

  if (rawAmount === "" || rawAmount === null || rawAmount === undefined) {
    return "--";
  }

  const amountText = String(rawAmount);
  return amountText.indexOf("¥") === 0 ? amountText : `¥${amountText}`;
}

function normalizeCaseItem(item, index) {
  const partyList = [item.plaintiff, item.defendant].filter(Boolean);

  return {
    rank: String(index + 1),
    cause:
      item.cause ||
      item.caseCause ||
      item.caseCauseName ||
      item.case_reason ||
      item.caseReason ||
      item.keyword ||
      "--",
    caseType: item.case_type || item.caseType || "--",
    court: item.court || item.court_name || "--",
    caseNo: item.caseNo || item.case_no || "--",
    judgeDate: item.judgmentDate || item.judgeDate || item.judge_date || "--",
    procedure:
      item.trialProcedure || item.procedure || item.trial_procedure || "--",
    parties:
      item.parties ||
      item.party_names ||
      (partyList.length ? partyList.join(" / ") : "--"),
    amount: formatAmount(item),
    summary:
      item.judgmentSummary ||
      item.summary ||
      item.case_summary ||
      item.abstract ||
      item.content ||
      "--",
    basis: item.legalBasis || item.basis || item.legal_basis || "--",
    expanded: index === 0,
    lineType: index > 2 ? "long" : "short",
  };
}

function normalizeOrderDetail(item, fallbackOrder = {}) {
  const caseList =
    item.cases ||
    item.case_list ||
    item.caseList ||
    item.list ||
    item.detailCases ||
    [];
  const orderNo = String(
    item.orderNo ||
    item.order_no ||
    item.order_id ||
    item.id ||
    fallbackOrder.orderNo ||
    fallbackOrder.id ||
    "",
  );
  const dateRange = formatDateRange(item);

  return {
    ...createEmptyOrder(),
    ...fallbackOrder,
    id: orderNo,
    orderNo,
    orderId: String(
      item.id ||
      item.order_id ||
      fallbackOrder.orderId ||
      fallbackOrder.id ||
      "",
    ),
    caseReason:
      item.caseCauseName ||
      item.keyword ||
      item.caseReason ||
      item.case_reason ||
      fallbackOrder.caseReason ||
      "--",
    location: item.province || item.location || fallbackOrder.location || "--",
    dateRange: dateRange !== "--" ? dateRange : fallbackOrder.dateRange || "--",
    caseCount: String(
      item.quantity ||
      item.caseCount ||
      item.case_count ||
      fallbackOrder.caseCount ||
      "0",
    ),
    amount: formatAmount(item),
    detailCases: Array.isArray(caseList)
      ? caseList.map((caseItem, index) => normalizeCaseItem(caseItem, index))
      : [],
  };
}

export default {
  name: "OrderDetailDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    order: {
      type: Object,
      default() {
        return createEmptyOrder();
      },
    },
    assets: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      activeRank: null,
      loading: false,
      detailOrder: createEmptyOrder(),
      lastRequestedOrderId: "",
    };
  },
  computed: {
    displayOrder() {
      return this.detailOrder;
    },
    displayCases() {
      return (this.displayOrder.detailCases || []).map((item) => ({
        ...item,
        expanded: item.rank === this.activeRank,
      }));
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        if (value) {
          this.fetchOrderDetail();
        } else {
          this.activeRank = null;
          this.lastRequestedOrderId = "";
        }
      },
    },
    order: {
      deep: true,
      handler() {
        const orderId = this.getRequestOrderId();

        if (this.visible && orderId && orderId !== this.lastRequestedOrderId) {
          this.fetchOrderDetail();
        }
      },
    },
  },
  methods: {
    handleClose() {
      this.$emit("close");
    },
    getRequestOrderId() {
      return this.order.orderId || this.order.id || this.order.orderNo || "";
    },
    async fetchOrderDetail() {
      const orderId = this.getRequestOrderId();

      this.detailOrder = normalizeOrderDetail({}, this.order || {});
      this.activeRank = null;

      if (!orderId) {
        return;
      }

      if (orderId === this.lastRequestedOrderId) {
        this.resetAccordion();
        return;
      }

      this.lastRequestedOrderId = orderId;

      this.loading = true;

      try {
        const res = await this.$api({
          url: "CaseCause/orderDetail",
          method: "get",
          data: {
            order_id: orderId,
          },
        });

        if (res.code === 200 && res.data) {
          this.detailOrder = normalizeOrderDetail(res.data, this.order || {});
          this.resetAccordion();
          return;
        }

        this.detailOrder = normalizeOrderDetail({}, this.order || {});
        this.resetAccordion();
        this.$message.warning(res.msg || "订单详情加载失败");
      } catch (error) {
        this.detailOrder = normalizeOrderDetail({}, this.order || {});
        this.resetAccordion();
        this.$message.error("订单详情加载失败，请稍后重试");
      } finally {
        this.loading = false;
      }
    },
    resetAccordion() {
      const firstExpandedItem = (this.displayOrder.detailCases || []).find(
        (item) => item.expanded,
      );
      const firstItem = (this.displayOrder.detailCases || [])[0];
      this.activeRank = firstExpandedItem
        ? firstExpandedItem.rank
        : firstItem
          ? firstItem.rank
          : null;
    },
    toggleCase(rank) {
      this.activeRank = this.activeRank === rank ? null : rank;
    },
  },
};
</script>

<style scoped>
.order-detail-dialog {
  position: fixed;
  inset: 0;
  z-index: 2200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  background: rgba(0, 0, 0, 0.5);
}

.panel {
  position: relative;
  box-sizing: border-box;
  width: 1217px;
  max-height: calc(100vh - 64px);
  padding: 26px 16px 34px 42px;
  overflow-y: auto;
  background: #ffffff;
}

.close {
  position: absolute;
  top: 26px;
  right: 58px;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
}

.close-icon {
  width: 14px;
  height: 14px;
}

.base {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 11px;
  margin: 31px 21px 0 1px;
  border: 1px solid #d4d4d4;
  border-radius: 8px;
  padding: 22px 28px 20px 43px;
  background: #fbfbfb;
}

.base-item {
  text-align: left;
}

.base-label {
  margin: 0;
  color: #666666;
  font-size: 18px;
  line-height: 25px;
}

.base-value {
  margin: 7px 0 0;
  color: #000000;
  font-size: 18px;
  line-height: 25px;
}

.section-title {
  margin: 29px 0 0;
  color: #333333;
  font-size: 16px;
  font-weight: 700;
  line-height: 19px;
}

.cases {
  margin-top: 25px;
}

.case+.case {
  margin-top: 17px;
}

.case.is-expanded {
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 9px 15px 23px 18px;
}

.case-head,
.case-collapsed {
  display: flex;
  align-items: center;
}

.case-trigger {
  width: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 43px;
  height: 43px;
  border-radius: 50%;
  background: #d92f24;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  line-height: 19px;
}

.case-line {
  margin-left: 18px;
}

.case-line-active {
  width: 136px;
  height: 15px;
}

.case-arrow {
  margin-left: auto;
  width: 17px;
  height: 12px;
  transition: transform 0.2s ease;
}

.case-arrow.is-expanded {
  transform: rotate(180deg);
}

.case-divider {
  margin-top: 12px;
}

.case-divider img {
  width: 100%;
  height: 1px;
}

.case-grid {
  display: grid;
  grid-template-columns: 78px minmax(150px, 1fr) 78px minmax(140px, 220px);
  row-gap: 29px;
  column-gap: 12px;
  margin-top: 35px;
}

.label,
.value {
  margin: 0;
  color: #333333;
  font-size: 16px;
  line-height: 22px;
}

.value-wide {
  grid-column: span 1;
}

.value-full {
  grid-column: span 3;
}

.case-collapsed {
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 9px 15px 7px 19px;
}
</style>
<style scoped src="./OrderDetailDialog.mobile.css"></style>
